import { Button } from '@understory-io/leaf/button';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type Hls from 'hls.js';
import { Image } from '~/ui/image';
import { VolumeOffIcon } from '~/ui/icons/volume-off';

export type GalleryMedia =
  | { type: 'image'; url: string }
  | { type: 'video'; url: string }
  | { type: 'unknown'; url: string };

type GalleryProps = {
  media: GalleryMedia[];
  /**
   * Called when the user clicks any media tile (or the "See all" button).
   * The consuming page is expected to open a lightbox starting at the given
   * index.
   */
  onMediaClick: (index: number) => void;
};

const DESKTOP_VISIBLE = 4;
const SAFE_MEDIA_SCHEMES = /^https?:/i;
// Tell hls.js to assume a fat pipe so its ABR picks the top variant from
// the first segment, instead of starting low and ramping up. Mirrors the
// legacy storefront's video.js setting (apps/web video-js.tsx). 100 Mbps in
// bits/sec.
const HIGH_BANDWIDTH_ESTIMATE = 100 * 1024 * 1024;

function isSafeMediaUrl(url: string): boolean {
  return SAFE_MEDIA_SCHEMES.test(url);
}

function isHlsUrl(url: string): boolean {
  // Detect HLS by pathname extension only — `https://x/foo.m3u8?bar` is HLS,
  // `https://x/foo.mp4#.m3u8` is not. URL parsing handles both cases.
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.m3u8');
  } catch {
    return false;
  }
}

/** Read by both the grid template and `tileSizes`, so what the browser is told
 *  a tile measures cannot drift from the track it is actually given. */
const MAIN_FR = 2;
const SIDE_FR = 1;
const TOTAL_FR = MAIN_FR + SIDE_FR;

/**
 * Compute the desktop grid template based on how many media items we have.
 * Returns inline-style values that are ignored on mobile (where the gallery
 * uses `display: flex`) and applied on tablet+ where it switches to grid.
 *
 *   1 item   → 1 column, 1 row, single block
 *   2 items  → main + 1 side (1 row each)
 *   3 items  → main + 2 sides (main spans both rows)
 *   4+ items → main + 3 sides (extras hidden via :nth-child below)
 */
function desktopTemplate(count: number): CSSProperties {
  if (count <= 1) {
    return { gridTemplateColumns: '1fr', gridTemplateRows: '1fr' };
  }
  const sideRows = Math.min(count - 1, DESKTOP_VISIBLE - 1);
  return {
    gridTemplateColumns: `${MAIN_FR}fr ${SIDE_FR}fr`,
    gridTemplateRows: `repeat(${sideRows}, 1fr)`,
  };
}

export function Gallery({ media, onMediaClick }: GalleryProps) {
  const intl = useIntl();
  if (media.length === 0) return null;

  const showSeeAll = media.length > 1;

  const galleryLabel = intl.formatMessage({
    defaultMessage: 'Gallery',
    description: 'Accessible name for the experience media gallery section',
  });

  return (
    <section
      aria-label={galleryLabel}
      className={galleryStyles}
      style={desktopTemplate(media.length)}
    >
      {media.map((item, index) => (
        <ClickableTile
          key={`${item.type}:${item.url}`}
          media={item}
          index={index}
          total={media.length}
          onClick={onMediaClick}
          priority={index === 0}
        />
      ))}

      {showSeeAll && (
        <div className={seeAllButtonStyles}>
          <Button
            appearance="contrast"
            size="small"
            onClick={() => onMediaClick(0)}
          >
            <FormattedMessage
              defaultMessage="See all"
              description="Action that opens a lightbox showing every image and video in the gallery"
            />
          </Button>
        </div>
      )}
    </section>
  );
}

type ClickableTileProps = {
  media: GalleryMedia;
  index: number;
  total: number;
  onClick: (index: number) => void;
  priority: boolean;
};

function ClickableTile({
  media,
  index,
  total,
  onClick,
  priority,
}: ClickableTileProps) {
  const intl = useIntl();
  const inner = (
    <GalleryMediaItem
      item={media}
      sizes={tileSizes(index, total)}
      priority={priority}
    />
  );
  if (!inner) return null;

  const label =
    media.type === 'video'
      ? intl.formatMessage(
          {
            defaultMessage: 'Open video {position} of {total}',
            description:
              'Accessible label for a clickable gallery video tile that opens the lightbox',
          },
          { position: index + 1, total }
        )
      : intl.formatMessage(
          {
            defaultMessage: 'Open photo {position} of {total}',
            description:
              'Accessible label for a clickable gallery photo tile that opens the lightbox',
          },
          { position: index + 1, total }
        );

  return (
    <button
      type="button"
      onClick={() => onClick(index)}
      aria-label={label}
      className={tileStyles}
    >
      {inner}
      <span className={mobileCounterStyles} aria-hidden="true">
        <Text style="caption" color="contrast">
          {index + 1} / {total}
        </Text>
      </span>
    </button>
  );
}

/**
 * `sizes` for a gallery tile. Mobile is a full-bleed scroller whose slides are
 * `flex: 0 0 100%`, so `100vw` is already right there. Tablet+ is the grid,
 * inside a `<PageCard>` whose content is `100vw - 48` capped at 1200px, with a
 * `tiny` (4px) gap — hence `100vw - 52` for the space the columns divide, and
 * a cap that bites at 1248px:
 *
 *   one item    the whole container                      → 1200px at the cap
 *   first tile  the `2fr` column, (container - 4) * 2/3  →  797px at the cap
 *   the rest    the `1fr` column, (container - 4) / 3    →  399px at the cap
 *
 * The capped figures are rounded up to 800/400 — over-declaring can only move
 * the browser to the next candidate up, never to a blurry one.
 */
function tileSizes(index: number, total: number): string {
  const [capped, fluid] =
    total <= 1
      ? ['1200px', 'calc(100vw - 48px)']
      : index === 0
        ? ['800px', `calc((100vw - 52px) * ${MAIN_FR} / ${TOTAL_FR})`]
        : ['400px', `calc((100vw - 52px) * ${SIDE_FR} / ${TOTAL_FR})`];

  return `(min-width: 1248px) ${capped}, (min-width: 768px) ${fluid}, 100vw`;
}

function GalleryMediaItem({
  item,
  sizes,
  priority,
}: {
  item: GalleryMedia;
  sizes: string;
  priority: boolean;
}): ReactNode {
  if (!isSafeMediaUrl(item.url)) return null;

  switch (item.type) {
    case 'video':
      return (
        <>
          <GalleryVideo url={item.url} priority={priority} />
          <span className={mutedIndicatorStyles} aria-hidden="true">
            <VolumeOffIcon size="medium" />
          </span>
        </>
      );
    case 'image':
      return (
        <Image
          src={item.url}
          alt=""
          layout="fullWidth"
          sizes={sizes}
          priority={priority}
          className={mediaStyles}
        />
      );
    case 'unknown':
      return null;
  }
}

function GalleryVideo({ url, priority }: { url: string; priority: boolean }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isHlsUrl(url)) return;

    // Safari/iOS plays HLS natively. The browser's `autoplay` attribute fires
    // once the source resolves — but we set `src` imperatively here, so help
    // it along with an explicit play() (silently ignore if blocked).
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = url;
      void video.play().catch(() => {});
      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    // Other browsers: lazy-load hls.js and attach. The native `autoplay`
    // attribute does not fire when the source is attached via Media Source
    // Extensions, so we kick playback off ourselves on MANIFEST_PARSED.
    let hls: Hls | null = null;
    let cancelled = false;
    void import('hls.js').then(({ default: HlsCtor }) => {
      if (cancelled || !HlsCtor.isSupported()) return;
      hls = new HlsCtor({ abrEwmaDefaultEstimate: HIGH_BANDWIDTH_ESTIMATE });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(HlsCtor.Events.MANIFEST_PARSED, () => {
        void video.play().catch(() => {});
      });
    });

    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [url]);

  // For HLS we attach via the effect above; for plain mp4/webm let the video
  // element load directly so it works without JS. Marked aria-hidden because
  // gallery videos are ambient muted loops with no captions or transcript —
  // the surrounding clickable button carries the accessible name.
  return (
    <video
      ref={videoRef}
      src={isHlsUrl(url) ? undefined : url}
      className={mediaStyles}
      autoPlay
      muted
      loop
      playsInline
      preload={priority ? 'auto' : 'metadata'}
      aria-hidden="true"
    />
  );
}

// One gallery, two layouts: a horizontal scroll-snap row on mobile (showing
// every item) and a 2-column grid on tablet+ (main image + up to 3 stacked
// side images, with "See all" overlay for the rest). Tiles are rendered once
// — this avoids double-fetching every image and double-mounting every
// `<video>`.
const galleryStyles = css({
  position: 'relative',

  // Mobile: full-bleed horizontal scroller
  display: { base: 'flex', tablet: 'grid' },
  flexDirection: { base: 'row', tablet: 'unset' },
  overflowX: { base: 'auto', tablet: 'hidden' },
  scrollSnapType: { base: '[x mandatory]', tablet: '[unset]' },
  width: { base: '[100vw]', tablet: 'full' },
  height: { base: '[350px]', tablet: '[unset]' },
  marginLeft: { base: '[calc(50% - 50vw)]', tablet: 'auto' },
  marginRight: { base: '[calc(50% - 50vw)]', tablet: 'auto' },

  // Tablet+: grid
  gap: { tablet: 'tiny' },
  aspectRatio: { tablet: '[16 / 10]' },
  maxHeight: { tablet: '[450px]' },
  borderRadius: { tablet: 'medium' },

  // First tile is the main desktop slot — spans every row of the side column
  // and stays in column 1. Ignored on mobile because flex children don't
  // honor grid-row / grid-column.
  '& > button:first-child': {
    gridRow: { tablet: '1 / -1' },
    gridColumn: { tablet: '1' },
  },

  // Hide tiles 5+ on desktop — they're still in the DOM (and reachable via
  // the mobile scroller and the lightbox) but aren't part of the desktop
  // grid. The `button` qualifier keeps the See-all wrapper (a div) visible.
  '& > button:nth-child(n+5)': {
    display: { tablet: 'none' },
  },
});

const tileStyles = css({
  position: 'relative',

  // Mobile: scroll-snap slide. flex-* and scroll-snap-* are ignored when
  // the parent switches to `display: grid` on tablet+, so no override needed.
  flex: '[0 0 100%]',
  scrollSnapAlign: 'start',
  width: 'full',
  height: 'full',

  // Grid items default to `min-height: max-content` — without this, a tall
  // image natural-aspect could push its 1fr track past the cell share and
  // clip the next row.
  minHeight: '[0]',
  overflow: 'hidden',

  // Reset native button defaults so tiles look like plain media containers.
  padding: 'none',
  margin: 'none',
  border: 'none',
  background: '[transparent]',
  cursor: 'interactive',
  appearance: 'none',
  textAlign: 'inherit',
  font: '[inherit]',
  color: '[inherit]',
});

// Media (image/video) elements are absolutely positioned to fill their tile.
// This sidesteps the `<img>` intrinsic-height behavior (and any inline
// styles unpic-react sets) so the rendered media always matches the tile's
// CSS-defined dimensions exactly; `object-fit: cover` crops as needed.
const mediaStyles = css({
  position: 'absolute',
  inset: '[0]',
  width: 'full',
  height: 'full',
  objectFit: 'cover',
  display: 'block',
});

const mutedIndicatorStyles = css({
  position: 'absolute',
  top: 'medium',
  right: 'medium',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 'xlarge',
  height: 'xlarge',
  borderRadius: 'full',
  background: 'surface.overlay',
  color: 'content.contrast',
  pointerEvents: 'none',
});

// Mobile-only "1 / 6" position counter overlay. Hidden on tablet+ where the
// grid layout makes position obvious from the visual hierarchy.
const mobileCounterStyles = css({
  position: 'absolute',
  top: 'medium',
  left: 'medium',
  paddingX: 'small',
  paddingY: 'tiny',
  borderRadius: 'small',
  background: 'surface.overlay',
  display: { base: 'block', tablet: 'none' },
});

// Desktop-only "See all" button. Positioned over the bottom-right of the
// grid; hidden on mobile where horizontal scrolling is the navigation.
const seeAllButtonStyles = css({
  position: 'absolute',
  bottom: 'medium',
  right: 'medium',
  display: { base: 'none', tablet: 'block' },
});
